import { useEffect, useState } from 'react';
import { ApiError, api } from '../api';
import type { MeResponse } from '@medistock/contracts';

/** Spec §6: pick a name, then a numeric PIN. Nothing else on the screen. */
export function Login({ onSignedIn }: { onSignedIn: (user: MeResponse) => void }) {
  const [staff, setStaff] = useState<{ id: number; code: string; displayName: string }[]>([]);
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .staff()
      .then((r) => setStaff(r.staff))
      .catch(() => setStaff([]));
  }, []);

  const press = (digit: string) => {
    setError(null);
    setPin((p) => (p.length >= 10 ? p : p + digit));
  };

  const submit = async (nextPin: string) => {
    if (!code || nextPin.length < 4 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.login(code, nextPin);
      onSignedIn(result.user);
    } catch (err) {
      setPin('');
      setError(err instanceof ApiError ? err.message : 'Sign in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app">
      <header className="bar">
        <h1>MediStock</h1>
      </header>
      <main>
        <div className="card">
          <h2>Who is recording?</h2>
          <div className="field">
            <label htmlFor="staff">Name</label>
            <select
              id="staff"
              data-testid="staff-select"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setPin('');
                setError(null);
              }}
            >
              <option value="">Select your name</option>
              {staff.map((s) => (
                <option key={s.id} value={s.code}>
                  {s.displayName}
                </option>
              ))}
            </select>
          </div>

          <label>PIN</label>
          <div className="pindots" aria-hidden="true">
            {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
              <i key={i} className={i < pin.length ? 'on' : ''} />
            ))}
          </div>

          <div className="pinpad">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button key={d} type="button" onClick={() => press(d)} disabled={!code || busy}>
                {d}
              </button>
            ))}
            <button type="button" className="ghost" onClick={() => setPin('')} disabled={busy}>
              Clear
            </button>
            <button type="button" onClick={() => press('0')} disabled={!code || busy}>
              0
            </button>
            <button
              type="button"
              className="primary"
              data-testid="pin-submit"
              disabled={!code || pin.length < 4 || busy}
              onClick={() => void submit(pin)}
            >
              {busy ? '…' : 'Enter'}
            </button>
          </div>

          {error ? (
            <>
              <div className="spacer" />
              <p className="notice error" role="alert" data-testid="login-error">
                {error}
              </p>
            </>
          ) : null}
        </div>
        <p className="small muted center">
          MediStock records product movement only. Never enter patient details.
        </p>
      </main>
    </div>
  );
}
